import React from "react";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { PAGE_NAME, ROUTE } from "../../../../../constants/constants";
import { Loader } from "../../../../atoms/loader";
import { useCreateCredentialMutation, useSleetProjectListQuery } from "../../../../../store/credentialsManagement/credentialsSlice";
import { Dropdown } from "../../../../atoms/dropdown";
import { payloadType, ResponseType } from "../../../../../types";
import { Input } from "../../../../atoms/input";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useUserListQuery } from "../../../../../store/user/userSlice";

export const CreateCredential = () => {
    const pageClassName = "p-createCredential";

    const navigate = useNavigate();

    const { data: users, isLoading: userListLoading } = useUserListQuery({});
    const [createAPI, { isLoading }] = useCreateCredentialMutation();
    const { data: projects, isLoading: projectLoading } = useSleetProjectListQuery({});

    const initialPayload: payloadType = {
        user: {
            value: "" as unknown as { value: string, label: string },
            error: "",
            isError: false,
        },
        email: {
            value: "",
            error: "",
            isError: false,
        },
        project: {
            value: "" as unknown as { value: string, label: string },
            error: "",
            isError: false,
        },
        password: {
            value: "",
            error: "",
            isError: false,
        },
        passwordConfirmation: {
            value: "",
            error: "",
            isError: false,
        },
    };

    const [payload, setPayload] = React.useState(initialPayload);

    const passwordChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

        if (!passwordRegex.test(e.target.value)) {
            if (e.target.value.length !== 0) {
                setPayload({
                    ...payload,
                    password: {
                        value: e.target.value,
                        error: "Password should be at least 8 characters long and should contain at least one uppercase, one lowercase, one number and one special character.",
                        isError: true,
                    },
                });
            } else {
                setPayload({
                    ...payload,
                    password: {
                        value: e.target.value,
                        error: "",
                        isError: false,
                    },
                });
            }
            return;
        }

        setPayload({
            ...payload,
            password: {
                value: e.target.value,
                error: "",
                isError: false,
            },
        });
    }

    const confirmationPasswordChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (payload.password.value !== e.target.value) {
            setPayload({
                ...payload,
                passwordConfirmation: {
                    value: e.target.value,
                    error: "Password does not match",
                    isError: true,
                },
            });
            return;
        }

        setPayload({
            ...payload,
            passwordConfirmation: {
                value: e.target.value,
                error: "",
                isError: false,
            },
        });
    }


    const submitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData();
        const toastId = toast.loading("Loading...");

        formData.append("email", payload.email.value as string);
        formData.append("password", payload.password.value as string);
        formData.append("password_confirmation", payload.passwordConfirmation.value as string);
        formData.append("project_id", (payload.project.value as { label: string, value: string }).value);
        const response = await createAPI(formData) as ResponseType;

        if (response?.data?.success) {
            setPayload(initialPayload);

            toast.update(toastId, {
                render: response?.data?.message,
                type: toast.TYPE.SUCCESS,
                isLoading: isLoading,
                autoClose: 2000,
            });
            navigate(ROUTE.CREDENTIALS_LIST);
        } else {
            toast.update(toastId, {
                render: response?.error?.data?.message,
                type: toast.TYPE.ERROR,
                isLoading: isLoading,
                autoClose: 2000,
            });

            if (response?.error?.data?.errors) {
                Object.keys(response?.error?.data?.errors).forEach((key) => {
                    setPayload((prevState) => ({
                        ...prevState,
                        [key]: {
                            ...prevState[key],
                            error: response?.error?.data?.errors[key][0],
                            isError: true,
                        },
                    }));
                });
            } else {
                toast.error(response?.error?.message);
            }
        }
    }
    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>{PAGE_NAME.CREATE_CREDENTIAL}</p>
            <Loader isLoading={isLoading || userListLoading || projectLoading}>
                <form className={`${pageClassName}__form`} onSubmit={submitHandler}>
                    <div className={`${pageClassName}__form__container`}>
                        <div className={`${pageClassName}__form__group`}>
                            <label className={`${pageClassName}__form__group__label`}>User</label>
                            <Dropdown
                                options={users?.result?.data.map((user: any) => ({
                                    label: user?.firstName + " " + user?.lastName,
                                    value: user?.email,
                                }))}
                                placeholder="Please select a user"
                                selected={payload.user.value as { label: string, value: string }}
                                onClickChange={(e) =>
                                    setPayload({
                                        ...payload,
                                        user: {
                                            value: e as { label: string, value: string },
                                            error: "",
                                            isError: false,
                                        },
                                        email: {
                                            value: e.value,
                                            error: "",
                                            isError: false,
                                        },
                                    })
                                }
                                isBordered
                                isError={payload.user.isError}
                                error={payload.user.error}
                            />
                        </div>

                        <div className={`${pageClassName}__form__group`}>
                            <label className={`${pageClassName}__form__group__label`}>Projects</label>
                            <Dropdown
                                options={projects?.result?.data.map((project: any) => ({
                                    label: project?.name,
                                    value: project?.id,
                                }))}
                                placeholder="Please select a project"
                                selected={payload.project.value as { label: string, value: string }}
                                onClickChange={(e) =>
                                    setPayload({
                                        ...payload,
                                        project: {
                                            value: e as { label: string, value: string },
                                            error: "",
                                            isError: false,
                                        }
                                    })
                                }
                                isBordered
                                isError={payload.project.isError}
                                error={payload.project.error}
                            />
                        </div>

                        <Input
                            label="Email"
                            placeholder="Email"
                            value={payload.email.value as string}
                            onChange={(e) => { }}
                            readonly
                            isError={payload.email.isError}
                            error={payload.email.error}
                        />

                        <Input
                            label="Password"
                            placeholder="Password"
                            onChange={passwordChangeHandler}
                            isError={payload.password.isError}
                            error={payload.password.error}
                        />

                        <Input
                            label="Confirm Password"
                            placeholder="Confirm Password"
                            onChange={confirmationPasswordChangeHandler}
                            isError={payload.passwordConfirmation.isError}
                            error={payload.passwordConfirmation.error}
                        />
                    </div>
                    <div className={`${pageClassName}__form__action`}>
                        <button
                            className={`${pageClassName}__form__action__button`}
                            type="submit"
                        >
                            Save
                        </button>
                    </div>
                </form>
            </Loader>

        </div>
    );
}